import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import FolderTabLayout from "@/components/ui/folder-tab-layout";
import { ClipboardPlus, ListChecks } from "lucide-react";
import MaintenanceRecordForm from "./MaintenanceRecordForm";
import MaintenanceReport from "./MaintenanceReport";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";

const MaintenanceTab: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
  // only sees/logs work for their linked vehicle(s), and only sees "Log
  // Work" at all with edit rights on Fleet -- logging a record is a
  // mutation, not a view.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "fleet");
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;

  const [tab, setTab] = useState<"log" | "records">(canEdit ? "log" : "records");
  const [vehicles, setVehicles] = useState<{ vehicle_number: string; registration: string | null }[]>([]);
  const [systems, setSystems] = useState<string[]>([]);
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadLookups = useCallback(async () => {
    let vQuery = supabase.from("vehicles").select("vehicle_number, registration").order("vehicle_number");
    if (scopedVehicleNumbers) {
      vQuery = vQuery.in("vehicle_number", scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"]);
    }
    const [vRes, sRes, wRes] = await Promise.all([
      vQuery,
      supabase.from("maintenance_systems").select("name").eq("is_active", true).order("sort_order").order("name"),
      supabase.from("maintenance_work_types").select("name").eq("is_active", true).order("sort_order").order("name"),
    ]);
    if (vRes.error || sRes.error || wRes.error) {
      toast({ title: "Error loading lists", description: (vRes.error || sRes.error || wRes.error)?.message, variant: "destructive" });
      return;
    }
    setVehicles((vRes.data || []) as { vehicle_number: string; registration: string | null }[]);
    setSystems(((sRes.data || []) as { name: string }[]).map((r) => r.name));
    setWorkTypes(((wRes.data || []) as { name: string }[]).map((r) => r.name));
  }, [scopedVehicleNumbers]);

  useEffect(() => {
    void loadLookups();
  }, [loadLookups]);

  const handleSaved = () => setRefreshKey((k) => k + 1);

  return (
    <FolderTabLayout
      tabs={[
        ...(canEdit ? [{ value: "log", label: "Log Work", icon: ClipboardPlus }] : []),
        { value: "records", label: "Records & Reports", icon: ListChecks },
      ]}
      value={tab}
      onValueChange={(v) => setTab(v as typeof tab)}
    >
      {tab === "log" && canEdit && (
        <MaintenanceRecordForm
          vehicles={vehicles}
          systems={systems}
          workTypes={workTypes}
          onSaved={handleSaved}
        />
      )}

      {tab === "records" && (
        <MaintenanceReport
          systems={systems}
          workTypes={workTypes}
          refreshKey={refreshKey}
          scopedVehicleNumbers={scopedVehicleNumbers}
          canEdit={canEdit}
        />
      )}
    </FolderTabLayout>
  );
};

export default MaintenanceTab;
